/**
 * The pure parts of /symbol/'s step form and its quick tutorial (SPEC.md §12.6), shared with the server where a
 * limit must hold on both sides. No DOM here: apps/label/test/setup.test.ts covers it.
 */

/** Re-draws allowed before the symbol is saved (the first draw is free): still random, never a picker. */
export const MAX_REDRAWS = 2;
/** The personal words: 1 to 60 characters (the server's `validPhrase` is the rule; this mirrors it for the UI). */
export const PHRASE_MAX = 60;
/** localStorage key: the quick tutorial was seen (or skipped) in this browser. */
export const TOUR_KEY = 'pageid.tour.v1';
export const TOUR_CARDS = 4;
export const SETUP_STEPS = 3;

/** The words as the server will store them (NFC, trimmed, runs of spaces collapsed) and whether Continue may go on. */
export function phraseState(raw: string): { text: string; count: number; ok: boolean } {
  const text = raw.normalize('NFC').trim().replace(/\s+/g, ' ');
  const count = [...text].length;
  const ok = count >= 1 && count <= PHRASE_MAX && !/[\p{Cc}\p{Cf}]/u.test(text);
  return { text, count, ok };
}

/** The counter under the input: characters typed (as the input holds them) of 60. */
export const counterText = (raw: string) => `${[...raw].length} / ${PHRASE_MAX}`;

/** How many re-draws are left after `used` of them. */
export const redrawsLeft = (used: number) => Math.max(0, MAX_REDRAWS - Math.max(0, Math.floor(used)));

/** The link under the symbol: the offer while re-draws are left, nothing once they are used up. */
export function redrawLabel(left: number): string | null {
  if (left <= 0) return null;
  return left === MAX_REDRAWS ? 'Not a fan? Draw another' : 'Draw one more (last one)';
}

/**
 * The shuffle before a symbol lands (like the landing page's demo): the delays between frames, short at first and
 * slowing down, ≈ 1 s in all; empty for reduced motion (the symbol is there at once).
 */
export function rollDelays(reduced: boolean, frames = 11): number[] {
  if (reduced) return [];
  const out: number[] = [];
  for (let i = 0; i < frames; i++) {
    const t = i / (frames - 1);
    out.push(Math.round(45 + 175 * t * t));
  }
  return out;
}

/** `count` different symbols for the shuffle, none of them `final` (decoration only: the server drew `final`). */
export function rollSymbols(final: number, count: number, total: number, rand: () => number = Math.random): number[] {
  const out: number[] = [];
  const seen = new Set([final]);
  let guard = 0;
  while (out.length < Math.min(count, total - 1) && guard++ < count * 50) {
    const n = Math.floor(rand() * total);
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

/** A symbol for the tutorial's fake pill: any symbol but yours. */
export function decoyFor(mine: number, total: number, rand: () => number = Math.random): number {
  if (total < 2) return mine;
  let n = Math.floor(rand() * total);
  while (n === mine) n = Math.floor(rand() * total);
  return n;
}

export type Step = 1 | 2 | 3;
export type Wizard = { step: Step; drawn: boolean; saved: boolean };

/** Which step may come next. Step 1 needs a drawn symbol, step 2 valid words (and is left only by saving). */
export function canAdvance(w: Wizard, phraseOk: boolean): boolean {
  if (w.step === 1) return w.drawn;
  if (w.step === 2) return phraseOk;
  return false;
}

/** Back from a step: 2 → 1 (the drawn symbol stays), 3 never goes back (the cookie is saved), 1 → leave. */
export function backFrom(step: Step): Step | 'leave' | null {
  if (step === 2) return 1;
  if (step === 1) return 'leave';
  return null;
}

/** "1 of 3" for the progress indicator. */
export const progressText = (step: number, of = SETUP_STEPS) => `Step ${step} of ${of}`;

/** The tutorial's card after a swipe/arrow, clamped to the cards. */
export const tourMove = (i: number, d: number) => Math.min(TOUR_CARDS - 1, Math.max(0, i + d));

/**
 * A horizontal swipe on the tutorial: −1 (next, swiped left), +1 (back), 0 (not a swipe). Mostly horizontal,
 * past 48 px or a flick faster than 0.5 px/ms.
 */
export function swipeStep(dx: number, dy: number, ms: number): -1 | 0 | 1 {
  if (Math.abs(dx) < 24 || Math.abs(dx) < Math.abs(dy) * 1.4) return 0;
  const fast = ms > 0 && Math.abs(dx) / ms > 0.5;
  if (Math.abs(dx) < 48 && !fast) return 0;
  return dx < 0 ? -1 : 1;
}

type Store = Pick<Storage, 'getItem' | 'setItem'>;
/** Has this browser seen the tutorial? Storage that throws or is missing counts as not seen. */
export function tourSeen(s: Store | null | undefined): boolean {
  try {
    return !!s && s.getItem(TOUR_KEY) === '1';
  } catch {
    return false;
  }
}
/** Marks the tutorial as seen; never throws. */
export function markTourSeen(s: Store | null | undefined): void {
  try {
    s?.setItem(TOUR_KEY, '1');
  } catch {}
}
